import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, ThumbsDown, ThumbsUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/States";
import ElmMark from "@/components/ElmMark";
import { PREF_KINDS } from "@/components/project/PreferencesTab";
import { SignoffButton, SignoffStamp, useSignoffs } from "@/components/project/Signoff";

export default function PreferenceSheet() {
  const { id } = useParams();
  const { brand } = useAuth();
  const project = useFetch(`/projects/${id}`);
  const prefs = useFetch(`/projects/${id}/preferences`);
  const signoffs = useSignoffs(id);
  if (!project.data || !prefs.data) return <Loading />;
  const rows = prefs.data;
  const likes = rows.filter((p) => p.sentiment === "like");
  const dislikes = rows.filter((p) => p.sentiment === "dislike");
  const authors = [...new Set(rows.filter((p) => p.created_by_role === "client").map((p) => p.created_by_name))];
  const date = new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="min-h-screen bg-white text-stone-900 print:bg-white" data-testid="preference-sheet">
      <div className="mx-auto flex max-w-[8.5in] items-center justify-between px-6 py-4 print:hidden">
        <Link to={`/projects/${id}?tab=prefs`} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="sheet-back"><ArrowLeft className="h-4 w-4" />Back to Design Preferences</Link>
        <div className="flex items-center gap-2"><SignoffButton projectId={id} kind="preferences" list={signoffs.data || []} onChange={signoffs.reload} /><Button size="sm" className="rounded-full" onClick={() => window.print()} data-testid="sheet-print"><Printer className="mr-1.5 h-3.5 w-3.5" />Print / Save PDF</Button></div>
      </div>
      <main className="mx-auto max-w-[8.5in] px-8 pb-12 pt-4 print:px-0 print:pt-0">
        <header className="flex items-start justify-between border-b border-stone-900 pb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500">{brand.name} · Design preferences</p>
            <h1 className="mt-1 font-serif text-4xl leading-none" data-testid="sheet-title">{project.data.name}</h1>
            {project.data.address && <p className="mt-1 text-sm text-stone-500">{project.data.address}</p>}
          </div>
          <div className="text-right text-xs text-stone-500"><p>{date}</p>{authors.length > 0 && <p className="mt-1">From {authors.join(", ")}</p>}</div>
        </header>
        <p className="mt-5 text-sm leading-relaxed text-stone-700">What you told us about your taste, in your own words. We'll use this to shape the proposal. Please check it over: anything missing or wrong, tell us before design starts.</p>

        <section className="mt-8">
          <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-stone-500"><ThumbsUp className="h-3.5 w-3.5" />What you love · {likes.length}</h2>
          {PREF_KINDS.map(([k, label]) => {
            const items = likes.filter((p) => p.kind === k);
            if (!items.length) return null;
            return (
              <div key={k} className="mt-5 break-inside-avoid" data-testid={`sheet-section-${k}`}>
                <h3 className="font-serif text-xl">{label}</h3>
                <ul className="mt-2 grid grid-cols-3 gap-4">
                  {items.map((p) => (
                    <li key={p.id} className="break-inside-avoid">
                      {p.image_url ? <img src={fileUrl(p.image_url)} alt={p.title} className="aspect-[4/3] w-full rounded-md object-cover" /> : p.hex ? <div className="aspect-[4/3] w-full rounded-md border" style={{ background: p.hex }} /> : null}
                      <p className="mt-1.5 text-sm font-medium leading-snug">{p.title || p.hex}</p>
                      {p.note && <p className="text-xs italic leading-snug text-stone-600">“{p.note}”</p>}
                      {p.reply && <p className="mt-1 text-[11px] text-stone-500">{brand.name}: {p.reply}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {likes.length === 0 && <p className="mt-3 text-sm text-stone-500">Nothing saved yet.</p>}
        </section>

        <section className="mt-10 break-inside-avoid">
          <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-stone-500"><ThumbsDown className="h-3.5 w-3.5" />Please avoid · {dislikes.length}</h2>
          <ul className="mt-3 columns-2 gap-6 text-sm">
            {dislikes.map((p) => <li key={p.id} className="mb-2 break-inside-avoid border-l-2 border-stone-300 pl-3"><span className="font-medium">{p.title || p.hex}</span>{p.note && <span className="text-stone-600"> — {p.note}</span>}<span className="ml-1 text-[10px] uppercase tracking-wider text-stone-400">{p.kind}</span></li>)}
            {dislikes.length === 0 && <li className="text-stone-500">Nothing to avoid noted.</li>}
          </ul>
        </section>

        <SignoffStamp list={signoffs.data || []} kind="preferences" className="mt-8" />
        <footer className="mt-12 grid grid-cols-2 gap-10 break-inside-avoid border-t pt-6 text-xs text-stone-500">
          <div>{(signoffs.data || []).some((x) => x.kind === "preferences") ? <p className="h-10 text-sm text-stone-900">{(signoffs.data || []).filter((x) => x.kind === "preferences").map((x) => `${x.name} — approved ${x.created_at.slice(0, 10)}`).join("; ")}</p> : <div className="h-10 border-b border-stone-400" />}<p className="mt-1">Confirmed by client · date</p></div>
          <div><div className="h-10 border-b border-stone-400" /><p className="mt-1">For {brand.name} · date</p></div>
          <div className="col-span-2 flex items-center justify-between"><ElmMark /><span>These are preferences, not design decisions. The proposal may interpret them.</span></div>
        </footer>
      </main>
    </div>
  );
}
